"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "sunlit-mode";
const SUNRISE_MS = 1000;
const SUNSET_MS = 1700;

export const SUNLIT_THEME_INIT_SCRIPT = `(function(){try{var t=localStorage.getItem("${STORAGE_KEY}");var d=t!=="light";document.documentElement.classList.toggle("dark",d);document.body.classList.toggle("sunlit-dark",d);document.body.style.backgroundColor=d?"#0f131c":"#fffdfa";}catch(e){}})();`;

const SunlitThemeContext = createContext(null);

let animationTimer;

function normalizeTheme(theme) {
  return theme === "light" ? "light" : "dark";
}

function readStoredTheme() {
  if (typeof window === "undefined") {
    return "dark";
  }

  return normalizeTheme(window.localStorage.getItem(STORAGE_KEY));
}

function applyTheme(theme, options = {}) {
  if (typeof document === "undefined") {
    return;
  }

  const { animate = false } = options;
  const isDark = theme === "dark";

  document.documentElement.classList.toggle("dark", isDark);
  document.body.classList.toggle("sunlit-dark", isDark);
  document.body.style.backgroundColor = isDark ? "#0f131c" : "#fffdfa";

  window.clearTimeout(animationTimer);

  if (animate) {
    document.body.classList.add("sunlit-animation-ready");
    animationTimer = window.setTimeout(() => {
      document.body.classList.remove("sunlit-animation-ready");
    }, isDark ? SUNSET_MS : SUNRISE_MS);
  } else {
    document.body.classList.remove("sunlit-animation-ready");
  }
}

export function SunlitThemeProvider({ children }) {
  // SSR 与首屏 hydration 必须一致；真实主题由 head 内联脚本先写入 DOM
  const [theme, setThemeState] = useState("dark");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme = readStoredTheme();
    setThemeState(storedTheme);
    applyTheme(storedTheme);
    setMounted(true);
    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY) {
        return;
      }

      const nextTheme = normalizeTheme(event.newValue);
      setThemeState(nextTheme);
      applyTheme(nextTheme);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const setTheme = useCallback((nextTheme, options = {}) => {
    const { animate = true } = options;
    const resolvedTheme = normalizeTheme(nextTheme);

    setThemeState(resolvedTheme);
    applyTheme(resolvedTheme, { animate });
    window.localStorage.setItem(STORAGE_KEY, resolvedTheme);
  }, []);

  const toggleTheme = useCallback((options = {}) => {
    setThemeState((current) => {
      const nextTheme = current === "dark" ? "light" : "dark";
      applyTheme(nextTheme, { animate: options.animate !== false });
      window.localStorage.setItem(STORAGE_KEY, nextTheme);
      return nextTheme;
    });
  }, []);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === "dark",
      mounted,
      setTheme,
      toggleTheme,
    }),
    [theme, mounted, setTheme, toggleTheme],
  );

  return (
    <SunlitThemeContext.Provider value={value}>
      {children}
    </SunlitThemeContext.Provider>
  );
}

export function useSunlitTheme() {
  const context = useContext(SunlitThemeContext);

  if (!context) {
    throw new Error("useSunlitTheme must be used within SunlitThemeProvider");
  }

  return context;
}
